package com.playoffpulse.frontend.ui

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.playoffpulse.frontend.routing.NhlChartRoute
import com.playoffpulse.shared.logic.displayLabel
import com.playoffpulse.shared.logic.PlayoffObjectiveResolver
import com.playoffpulse.shared.model.BlockedShotsMissesMode
import com.playoffpulse.shared.model.BlockedShotsChartResponse
import com.playoffpulse.shared.model.BlockedShotsChartRow
import com.playoffpulse.shared.model.BlockedShotsTeamDetailResponse
import com.playoffpulse.shared.model.BlockedShotsTeamGameRow
import com.playoffpulse.shared.model.DraftStandingsState
import com.playoffpulse.shared.model.DraftStandingsSummary
import com.playoffpulse.shared.model.LeagueId
import com.playoffpulse.shared.model.MagicStatus
import com.playoffpulse.shared.model.SnapshotMeta
import com.playoffpulse.shared.model.StandingsInsightsResponse
import com.playoffpulse.shared.model.TeamRef
import com.playoffpulse.shared.model.TeamStanding
import com.playoffpulse.shared.model.TeamPerspective
import com.playoffpulse.shared.model.YesterdayMovementEntry
import com.playoffpulse.shared.model.YesterdayMovementResponse
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import kotlin.math.abs
import kotlin.math.hypot
import kotlin.js.Date
import org.w3c.dom.Node
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

enum class NhlAppPage {
    TEAM,
    MOVEMENT,
    STANDINGS,
    CHARTS,
    ABOUT,
    FAQ,
}

internal enum class StandingsViewMode {
    LEAGUE,
    CONFERENCE,
    DIVISION,
}

internal enum class StandingsObjective {
    PLAYOFF,
    DRAFT,
}

internal enum class StandingsSortKey {
    TEAM,
    PTS,
    MAX,
}

internal enum class StandingsSortDirection {
    ASC,
    DESC,
}

internal data class StandingsSort(
    val key: StandingsSortKey = StandingsSortKey.PTS,
    val direction: StandingsSortDirection = StandingsSortDirection.DESC,
)

@Composable
fun PageNavigation(
    activePage: NhlAppPage,
    onOpenTeam: () -> Unit,
    onOpenMovement: () -> Unit,
    onOpenStandings: () -> Unit,
    onOpenCharts: () -> Unit,
    onOpenAbout: () -> Unit,
    onOpenFaq: () -> Unit,
    shareButtonLabel: String,
    onCopyUrl: () -> Unit,
) {
    Div(attrs = { classes("page-nav") }) {
        Button(
            attrs = {
                classes("page-nav-button")
                if (activePage == NhlAppPage.TEAM) {
                    classes("page-nav-button-active")
                }
                onClick { onOpenTeam() }
            },
        ) {
            Text("Teams")
        }
        Button(
            attrs = {
                classes("page-nav-button")
                if (activePage == NhlAppPage.MOVEMENT) {
                    classes("page-nav-button-active")
                }
                onClick { onOpenMovement() }
            },
        ) {
            Text("Movement")
        }
        Button(
            attrs = {
                classes("page-nav-button")
                if (activePage == NhlAppPage.STANDINGS) {
                    classes("page-nav-button-active")
                }
                onClick { onOpenStandings() }
            },
        ) {
            Text("Standings")
        }
        Button(
            attrs = {
                classes("page-nav-button")
                if (activePage == NhlAppPage.CHARTS) {
                    classes("page-nav-button-active")
                }
                onClick { onOpenCharts() }
            },
        ) {
            Text("Charts")
        }
        Button(
            attrs = {
                classes("page-nav-button")
                if (activePage == NhlAppPage.FAQ) {
                    classes("page-nav-button-active")
                }
                onClick { onOpenFaq() }
            },
        ) {
            Text("FAQ")
        }
        Button(
            attrs = {
                classes("page-nav-button")
                if (activePage == NhlAppPage.ABOUT) {
                    classes("page-nav-button-active")
                }
                onClick { onOpenAbout() }
            },
        ) {
            Text("About")
        }
        Button(
            attrs = {
                classes("page-nav-button", "page-nav-copy-button")
                if (shareButtonLabel == "Copied") {
                    classes("page-nav-copy-button-copied")
                }
                onClick { onCopyUrl() }
            },
        ) {
            Text(shareButtonLabel)
        }
    }
}

@Composable
fun YesterdayMovementSection(response: YesterdayMovementResponse?) {
    var expanded by androidx.compose.runtime.remember { androidx.compose.runtime.mutableStateOf(false) }

    Div(attrs = { classes("important-card") }) {
        H2 { Text("Movement") }

        when {
            response == null -> {
                P(attrs = { classes("meta-line") }) { Text("Loading movement feed...") }
            }

            !response.available -> {
                P(attrs = { classes("meta-line") }) {
                    Text("Movement will appear once two nightly snapshots are available.")
                }
            }

            response.movement.isEmpty() -> {
                P(attrs = { classes("meta-line") }) {
                    Text("No playoff movement registered between the last two nightly snapshots.")
                }
            }

            else -> {
                P(attrs = { classes("meta-line") }) {
                    Text(
                        "Compared against snapshot date ${response.comparedSnapshotDateEt ?: "prior night"}, ranked by status/seat change and clinch movement.",
                    )
                }

                val visibleRows = if (expanded) response.movement else response.movement.take(10)
                Div(attrs = { classes("movement-list") }) {
                    visibleRows.forEach { entry ->
                        YesterdayMovementRow(entry)
                    }
                }

                if (response.movement.size > 10) {
                    Button(
                        attrs = {
                            classes("pulse-button", "movement-toggle")
                            onClick { expanded = !expanded }
                        },
                    ) {
                        Text(if (expanded) "Show Top 10" else "Show All (${response.movement.size})")
                    }
                }
            }
        }
    }
}

@Composable
private fun YesterdayMovementRow(entry: YesterdayMovementEntry) {
    Div(attrs = { classes("movement-row") }) {
        Div(attrs = { classes("movement-header") }) {
            Div(attrs = { classes("movement-team") }) {
                Img(
                    src = TeamTilePalette.logoUrl(entry.team.id),
                    attrs = {
                        classes("movement-team-logo")
                        attr("alt", "${entry.team.displayName} logo")
                        attr("loading", "lazy")
                    },
                )
                Div {
                    P(attrs = { classes("movement-team-name") }) { Text(entry.team.displayName) }
                    P(attrs = { classes("movement-team-meta") }) {
                        Text(entry.currentSeatCode ?: entry.previousSeatCode ?: statusShort(entry.currentStatus))
                    }
                }
            }
            Span(attrs = { classes("movement-bucket") }) {
                Text(entry.significanceBucket.name.replace('_', ' '))
            }
        }

        P(attrs = { classes("movement-headline") }) {
            Text(entry.headline)
        }

        Div(attrs = { classes("movement-chips") }) {
            if (entry.previousStatus != entry.currentStatus) {
                MovementChip("Status", "${statusShort(entry.previousStatus)} -> ${statusShort(entry.currentStatus)}", "neutral")
            }
            if (entry.previousSeatCode != entry.currentSeatCode) {
                MovementChip("Seat", "${entry.previousSeatCode ?: "—"} -> ${entry.currentSeatCode ?: "—"}", "neutral")
            }
            if (entry.magicPointsDelta != 0) {
                val tone = if (entry.magicPointsDelta < 0) "good" else "bad"
                MovementChip("To Clinch", signedPts(entry.magicPointsDelta), tone)
            }
            if (entry.tragicPointsDelta != 0) {
                val tone = if (entry.tragicPointsDelta > 0) "good" else "bad"
                MovementChip("To Eliminate", signedPts(entry.tragicPointsDelta), tone)
            }
        }
    }
}

@Composable
private fun MovementChip(label: String, value: String, tone: String) {
    Span(attrs = { classes("movement-chip", "movement-chip-$tone") }) {
        Text("$label $value")
    }
}

@Composable
internal fun StandingsPage(
    teams: List<TeamRef>,
    standings: List<TeamStanding>,
    standingsInsights: StandingsInsightsResponse?,
    snapshotMeta: SnapshotMeta?,
    selectedTeamId: String?,
    objective: StandingsObjective,
    onObjectiveChange: (StandingsObjective) -> Unit,
    mode: StandingsViewMode,
    onModeChange: (StandingsViewMode) -> Unit,
    sort: StandingsSort,
    onSortChange: (StandingsSort) -> Unit,
    sectionFilter: String? = null,
) {
    if (teams.isEmpty() || standings.isEmpty() || snapshotMeta == null || standingsInsights == null) {
        Div(attrs = { classes("important-card") }) {
            H2 { Text("Standings") }
            P(attrs = { classes("meta-line") }) { Text("Loading standings table...") }
        }
        return
    }

    val rows = remember(teams, standings, standingsInsights) {
        buildStandingsRows(teams, standings, standingsInsights)
    }
    val sections = remember(rows, mode, sort, objective, sectionFilter) {
        buildStandingsSections(rows, mode, sort, objective, sectionFilter)
    }

    Div(attrs = { classes("important-card") }) {
        H2 { Text("Standings") }
        P(attrs = { classes("meta-line") }) {
            Text(
                when (objective) {
                    StandingsObjective.PLAYOFF -> "Nightly snapshot table with the same playoff math used in each team view."
                    StandingsObjective.DRAFT -> "Nightly snapshot table with the same draft-position math used in draft-first team views."
                },
            )
        }

        Div(attrs = { classes("standings-toggle-row") }) {
            StandingsModeButton("Playoff", objective == StandingsObjective.PLAYOFF) {
                onObjectiveChange(StandingsObjective.PLAYOFF)
            }
            StandingsModeButton("Draft", objective == StandingsObjective.DRAFT) {
                onObjectiveChange(StandingsObjective.DRAFT)
            }
        }

        Div(attrs = { classes("standings-toggle-row") }) {
            StandingsModeButton("League", mode == StandingsViewMode.LEAGUE) { onModeChange(StandingsViewMode.LEAGUE) }
            StandingsModeButton("Conference", mode == StandingsViewMode.CONFERENCE) { onModeChange(StandingsViewMode.CONFERENCE) }
            StandingsModeButton("Division", mode == StandingsViewMode.DIVISION) { onModeChange(StandingsViewMode.DIVISION) }
        }

        sections.forEach { section ->
            Div(attrs = { classes("standings-section") }) {
                H3(attrs = { classes("standings-section-title") }) { Text(section.title) }
                Div(attrs = { classes("standings-table-wrap") }) {
                    Table(attrs = { classes("standings-table") }) {
                        Thead {
                            Tr {
                                SortableStandingsHeader("Team", StandingsSortKey.TEAM, sort) {
                                    onSortChange(nextStandingsSort(sort, StandingsSortKey.TEAM, objective))
                                }
                                PointsMaxStandingsHeader(
                                    activeSort = sort,
                                    onSelectPoints = {
                                        onSortChange(nextStandingsSort(sort, StandingsSortKey.PTS, objective))
                                    },
                                    onSelectMax = {
                                        onSortChange(nextStandingsSort(sort, StandingsSortKey.MAX, objective))
                                    },
                                )
                                Th { Text("RW/Max") }
                                Th { Text("ROW/Max") }
                                Th { Text("W/Max") }
                                Th { Text("Status") }
                                Th { Text("Next Goal") }
                                Th { Text(if (objective == StandingsObjective.PLAYOFF) "To Clinch" else "To Better Tag") }
                                Th { Text(if (objective == StandingsObjective.PLAYOFF) "Cut Line" else "Better Tag Line") }
                                Th { Text(if (objective == StandingsObjective.PLAYOFF) "To Eliminate" else "To Slip") }
                                Th { Text("Path") }
                            }
                        }
                        Tbody {
                            section.rows.forEach { row ->
                                Tr(attrs = {
                                    if (row.team.id.equals(selectedTeamId, ignoreCase = true)) {
                                        classes("standings-row-selected")
                                    }
                                }) {
                                    Td {
                                        Div(attrs = { classes("standings-team-cell") }) {
                                            Img(
                                                src = TeamTilePalette.logoUrl(row.team.id),
                                                attrs = {
                                                    classes("standings-team-logo")
                                                    attr("alt", "${row.team.displayName} logo")
                                                    attr("loading", "lazy")
                                                },
                                            )
                                            Div {
                                                Span(attrs = { classes("standings-team-name") }) { Text(row.team.displayName) }
                                                Span(attrs = { classes("standings-team-abbrev") }) { Text(row.team.abbreviation) }
                                            }
                                        }
                                    }
                                    Td { Text(pointsMaxDisplay(row.playoffSummary)) }
                                    Td { Text(standingsTiebreakDisplay(row.standing.regulationWins, row.standing)) }
                                    Td { Text(standingsTiebreakDisplay(row.standing.regulationPlusOtWins, row.standing)) }
                                    Td { Text(standingsTiebreakDisplay(row.standing.wins, row.standing)) }
                                    Td {
                                        Text(
                                            when (objective) {
                                                StandingsObjective.PLAYOFF -> ShareFormatter.statusLabel(row.playoffSummary)
                                                StandingsObjective.DRAFT -> row.draftSummary.statusLabel
                                            },
                                        )
                                    }
                                    Td {
                                        Text(
                                            when (objective) {
                                                StandingsObjective.PLAYOFF -> PlayoffObjectiveResolver.nextGoalCode(row.playoffSummary) ?: "—"
                                                StandingsObjective.DRAFT -> row.draftSummary.nextGoalLabel ?: "—"
                                            },
                                        )
                                    }
                                    Td {
                                        Text(
                                            when (objective) {
                                                StandingsObjective.PLAYOFF -> row.playoffSummary.magicPointsToClinch.toString()
                                                StandingsObjective.DRAFT -> row.draftSummary.toBetterTagPoints?.toString() ?: "—"
                                            },
                                        )
                                    }
                                    Td {
                                        Text(
                                            when (objective) {
                                                StandingsObjective.PLAYOFF -> row.playoffSummary.conferenceCutLinePoints.toString()
                                                StandingsObjective.DRAFT -> row.draftSummary.betterTagLinePoints?.toString() ?: "—"
                                            },
                                        )
                                    }
                                    Td {
                                        Text(
                                            when (objective) {
                                                StandingsObjective.PLAYOFF -> row.playoffSummary.tragicPointsToElimination.toString()
                                                StandingsObjective.DRAFT -> row.draftSummary.toSlipPoints?.toString() ?: "—"
                                            },
                                        )
                                    }
                                    Td {
                                        Text(
                                            when (objective) {
                                                StandingsObjective.PLAYOFF -> pathTableText(row.playoffSummary)
                                                StandingsObjective.DRAFT -> row.draftSummary.pathLabel ?: "—"
                                            },
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun AboutPage(
    snapshotMeta: SnapshotMeta?,
    appVersion: String,
) {
    Div(attrs = { classes("important-card") }) {
        H2 { Text("About") }
        Div(attrs = { classes("info-block") }) {
            P(attrs = { classes("faq-answer") }) {
                Text(
                    "I built this tool to learn how to use Codex and Kotlin Compose for Web. I also like to count down the number of games my favorite teams need to win to get into the playoffs, and although there are good resources for that, I always want that little extra bit of data they do not have.",
                )
            }
            P(attrs = { classes("faq-answer") }) {
                Text("That made Playoff Pulse a great learning-project app. If you have comments or spot something wrong, please reach out on LinkedIn or Discord.")
            }
            P(attrs = { classes("faq-answer", "about-signature") }) {
                Text("Best, James Terrell")
            }
            P(attrs = { classes("faq-answer", "about-link-row") }) {
                A(
                    href = "https://linkedin.com/in/terrell",
                    attrs = {
                        classes("about-link")
                        attr("target", "_blank")
                        attr("rel", "noopener noreferrer")
                    },
                ) {
                    Text("linkedin.com/in/terrell")
                }
            }
            P(attrs = { classes("faq-answer", "about-link-row") }) {
                A(
                    href = "https://discord.gg/9WUVWdc4re",
                    attrs = {
                        classes("about-link")
                        attr("target", "_blank")
                        attr("rel", "noopener noreferrer")
                    },
                ) {
                    Text("discord.gg/9WUVWdc4re")
                }
            }
        }
        P(attrs = { classes("meta-line") }) {
            Text("Playoff Pulse is a nightly NHL playoff-race tracker built around points-based clinch and elimination math.")
        }

        Div(attrs = { classes("info-block") }) {
            H3 { Text("How To Use Each View") }
            P(attrs = { classes("faq-answer") }) {
                Text("Teams: pick one club and follow its current seat, clinch path, earliest clinch scenario, top rivals, and important games.")
            }
            P(attrs = { classes("faq-answer") }) {
                Text("Movement: review the biggest changes from the most recent completed night, sorted by playoff significance.")
            }
            P(attrs = { classes("faq-answer") }) {
                Text("Standings: compare every team in league, conference, or division tables using the same metrics shown on the team view.")
            }
            P(attrs = { classes("faq-answer") }) {
                Text("FAQ: read the playoff-math definitions and modeling notes behind the numbers.")
            }
            P(attrs = { classes("faq-answer") }) {
                Text("About: check the current data source, refresh cadence, and app version.")
            }
        }

        Div(attrs = { classes("info-block") }) {
            H3 { Text("Current Data") }
            if (snapshotMeta == null) {
                P(attrs = { classes("faq-answer") }) { Text("Loading snapshot metadata...") }
            } else {
                P(attrs = { classes("faq-answer") }) { Text("Source: ${snapshotMeta.source}") }
                P(attrs = { classes("faq-answer") }) { Text("As-of: ${snapshotMeta.asOfLabel}") }
                P(attrs = { classes("faq-answer") }) { Text("Cadence: ${snapshotMeta.refreshCadence}") }
            }
            P(attrs = { classes("faq-answer") }) {
                Text("Snapshots update after the prior night's games complete.")
            }
        }

        Div(attrs = { classes("info-block") }) {
            H3 { Text("Version") }
            P(attrs = { classes("faq-answer") }) { Text("App version: $appVersion") }
        }
    }
}

@Composable
fun FaqPage() {
    Div(attrs = { classes("important-card") }) {
        H2 { Text("FAQ") }
        FaqItem(
            question = "What does To Clinch (Any Seat) mean?",
            answers = listOf(
                "It is the number of standings points a team still needs to guarantee any playoff berth based on the latest nightly snapshot.",
                "Requirement labels also show the equivalent games needed, using half-games when the point total is odd.",
            ),
        )
        FaqItem(
            question = "How do magic points and magic wins relate?",
            answers = listOf(
                "Two standings points come from a win, so odd point requirements translate to half-games, such as 1pt = 0.5 games and 23pts = 11.5 games.",
                "That still stays points-first; overtime losses can add one point at a time.",
            ),
        )
        FaqItem(
            question = "What is the Clinch Target?",
            answers = listOf(
                "It is the point total that would put the team out of reach for its current objective.",
                "Before a berth is clinched, that objective is any playoff seat. After that, the app rolls forward to the next higher goal.",
            ),
        )
        FaqItem(
            question = "Why do chasing teams show a different path?",
            answers = listOf(
                "A chasing team does not control its own destiny, so a pure must-win count can be misleading even when every remaining game matters.",
                "For those teams, Playoff Pulse emphasizes survival pressure instead: they need wins, outside help, and enough room before their elimination number runs out.",
            ),
        )
        FaqItem(
            question = "How are Important Games chosen?",
            answers = listOf(
                "Immediate games lower the current clinch number right away, either because the team can gain points or a top rival can lose them.",
                "Future games add cushion but do not immediately reduce the current clinch number.",
            ),
        )
        FaqItem(
            question = "Why are the numbers based on nightly snapshots instead of live games?",
            answers = listOf(
                "The app ingests once after each night's games. That keeps the race math stable and avoids half-updated live states.",
                "Each view reflects the latest completed nightly snapshot.",
            ),
        )
        FaqItem(
            question = "Does the app simulate every NHL tiebreaker?",
            answers = listOf(
                "For live standings with uneven games played, the NHL can still rank clubs by the official live order before you ever reach the end-of-season tiebreak chain.",
                "Once teams are tied on points in the finish-style comparison, the NHL order is regulation wins (RW), regulation-plus-overtime wins (ROW), total wins, head-to-head points, goal differential, then goals for.",
                "Playoff Pulse currently surfaces the three win-based layers it can lock cleanly from the nightly snapshot: RW, then ROW, then Wins.",
                "Head-to-head and lower criteria are still treated as unresolved unless the math makes them fully locked, so those lower tiebreaks are not yet modeled as general-purpose race inputs.",
                "External sites can sometimes show lower magic-win numbers because remaining schedules force rivals to take points from each other. Playoff Pulse still treats rival point ceilings independently in that phase.",
            ),
        )
        FaqItem(
            question = "What do Leaders, Live, and Locked Behind mean?",
            answers = listOf(
                "Leaders are teams currently controlling a clear objective at the top of the race, such as a playoff seat, division lead, or draft-position lane.",
                "Live teams are still in play but not yet locked into either extreme. This is the app's documented name for the mushy middle.",
                "Locked Behind teams are teams the selected club has already locked behind for the current objective.",
                "On draft-first pages, the same idea appears as Locked Below because the race direction flips.",
            ),
        )
        FaqItem(
            question = "What do the status labels mean?",
            answers = listOf(
                "Clinched means the team has already locked the listed seat or better.",
                "Holds means the team is currently in a playoff position and still controls that slot.",
                "Chasing means the team is still alive but needs outside help.",
                "Eliminated means no points path remains to the relevant objective.",
            ),
        )
    }
}

@Composable
private fun StandingsModeButton(
    label: String,
    active: Boolean,
    onSelect: () -> Unit,
) {
    Button(
        attrs = {
            classes("tab-button")
            if (active) {
                classes("tab-button-active")
            }
            onClick { onSelect() }
        },
    ) {
        Text(label)
    }
}

@Composable
private fun FaqItem(
    question: String,
    answers: List<String>,
) {
    Div(attrs = { classes("faq-item") }) {
        H3(attrs = { classes("faq-question") }) { Text(question) }
        answers.forEach { answer ->
            P(attrs = { classes("faq-answer") }) { Text(answer) }
        }
    }
}

internal fun standingsTiebreakDisplay(
    currentValue: Int?,
    standing: TeamStanding,
): String {
    if (currentValue == null) return "—/—"
    val maxValue = currentValue + standing.gamesRemaining
    return "$currentValue/$maxValue"
}

internal fun pointsMaxDisplay(
    summary: com.playoffpulse.shared.model.MagicSummary,
): String {
    return "${summary.currentPoints}/${summary.maxPossiblePoints}"
}

internal data class StandingsRow(
    val team: TeamRef,
    val standing: TeamStanding,
    val playoffSummary: com.playoffpulse.shared.model.MagicSummary,
    val teamPerspective: TeamPerspective,
    val draftSummary: DraftStandingsSummary,
)

internal data class StandingsSection(
    val title: String,
    val rows: List<StandingsRow>,
)

@Composable
private fun SortableStandingsHeader(
    label: String,
    key: StandingsSortKey,
    activeSort: StandingsSort,
    onSelect: () -> Unit,
) {
    val isActive = activeSort.key == key
    val arrow = when {
        !isActive -> ""
        activeSort.direction == StandingsSortDirection.ASC -> " ↑"
        else -> " ↓"
    }

    Th {
        Button(
            attrs = {
                classes("standings-sort-button")
                if (isActive) {
                    classes("standings-sort-button-active")
                }
                onClick { onSelect() }
            },
        ) {
            Text(label + arrow)
        }
    }
}

@Composable
private fun PointsMaxStandingsHeader(
    activeSort: StandingsSort,
    onSelectPoints: () -> Unit,
    onSelectMax: () -> Unit,
) {
    Th {
        Div(attrs = { classes("standings-merged-header") }) {
            Span(attrs = { classes("standings-merged-title") }) {
                Text("PTS/Max")
            }
            Div(attrs = { classes("standings-merged-actions") }) {
                StandingsHeaderMiniButton(
                    label = "PTS",
                    key = StandingsSortKey.PTS,
                    activeSort = activeSort,
                    onSelect = onSelectPoints,
                )
                StandingsHeaderMiniButton(
                    label = "MAX",
                    key = StandingsSortKey.MAX,
                    activeSort = activeSort,
                    onSelect = onSelectMax,
                )
            }
        }
    }
}

@Composable
private fun StandingsHeaderMiniButton(
    label: String,
    key: StandingsSortKey,
    activeSort: StandingsSort,
    onSelect: () -> Unit,
) {
    val isActive = activeSort.key == key
    val arrow = when {
        !isActive -> ""
        activeSort.direction == StandingsSortDirection.ASC -> " ↑"
        else -> " ↓"
    }

    Button(
        attrs = {
            classes("standings-sort-button", "standings-sort-mini")
            if (isActive) {
                classes("standings-sort-button-active")
            }
            onClick { onSelect() }
        },
    ) {
        Text(label + arrow)
    }
}

private fun buildStandingsRows(
    teams: List<TeamRef>,
    standings: List<TeamStanding>,
    insights: StandingsInsightsResponse,
): List<StandingsRow> {
    val standingsById = standings.associateBy { it.teamId.uppercase() }
    val insightsById = insights.rows.associateBy { it.teamId.uppercase() }
    return teams.mapNotNull { team ->
        val standing = standingsById[team.id.uppercase()] ?: return@mapNotNull null
        val insight = insightsById[team.id.uppercase()] ?: return@mapNotNull null
        StandingsRow(
            team = team,
            standing = standing,
            playoffSummary = insight.playoffSummary,
            teamPerspective = insight.teamPerspective,
            draftSummary = insight.draftSummary,
        )
    }
}

internal fun buildStandingsSections(
    rows: List<StandingsRow>,
    mode: StandingsViewMode,
    sort: StandingsSort,
    objective: StandingsObjective,
    sectionFilter: String? = null,
): List<StandingsSection> {
    val sortedComparator = standingsComparator(sort, objective)
    val normalizedSectionFilter = sectionFilter
        ?.trim()
        ?.lowercase()
        ?.takeIf { it.isNotBlank() }

    val sections = when (mode) {
        StandingsViewMode.LEAGUE ->
            listOf(StandingsSection("League", rows.sortedWith(sortedComparator)))

        StandingsViewMode.CONFERENCE ->
            rows.groupBy { it.team.conference }
                .entries
                .sortedBy { it.key }
                .map { entry ->
                    StandingsSection(entry.key, entry.value.sortedWith(sortedComparator))
                }

        StandingsViewMode.DIVISION ->
            rows.groupBy { it.team.division }
                .entries
                .sortedBy { it.key }
                .map { entry ->
                    StandingsSection(entry.key, entry.value.sortedWith(sortedComparator))
                }
    }

    return if (normalizedSectionFilter == null || mode == StandingsViewMode.LEAGUE) {
        sections
    } else {
        sections.filter { it.title.trim().lowercase() == normalizedSectionFilter }
    }
}

internal fun nextStandingsSort(
    current: StandingsSort,
    selectedKey: StandingsSortKey,
    objective: StandingsObjective,
): StandingsSort {
    if (current.key == selectedKey) {
        return current.copy(
            direction = when (current.direction) {
                StandingsSortDirection.ASC -> StandingsSortDirection.DESC
                StandingsSortDirection.DESC -> StandingsSortDirection.ASC
            },
        )
    }

    return StandingsSort(
        key = selectedKey,
        direction = defaultStandingsSortDirection(selectedKey, objective),
    )
}

private fun defaultStandingsSortDirection(
    key: StandingsSortKey,
    objective: StandingsObjective,
): StandingsSortDirection = when (objective) {
    StandingsObjective.PLAYOFF -> when (key) {
        StandingsSortKey.TEAM -> StandingsSortDirection.ASC
        StandingsSortKey.PTS, StandingsSortKey.MAX -> StandingsSortDirection.DESC
    }

    StandingsObjective.DRAFT -> when (key) {
        StandingsSortKey.TEAM -> StandingsSortDirection.ASC
        StandingsSortKey.PTS, StandingsSortKey.MAX -> StandingsSortDirection.ASC
    }
}

private fun standingsComparator(
    sort: StandingsSort,
    objective: StandingsObjective,
): Comparator<StandingsRow> {
    return Comparator { left, right ->
        val primary = when (sort.key) {
            StandingsSortKey.TEAM -> compareStrings(left.team.displayName, right.team.displayName, sort.direction)
            StandingsSortKey.PTS -> compareInts(left.playoffSummary.currentPoints, right.playoffSummary.currentPoints, sort.direction)
            StandingsSortKey.MAX -> compareInts(left.playoffSummary.maxPossiblePoints, right.playoffSummary.maxPossiblePoints, sort.direction)
        }

        if (primary != 0) {
            primary
        } else {
            val fallback = when (objective) {
                StandingsObjective.PLAYOFF -> liveStandingComparator.compare(left.standing, right.standing)
                StandingsObjective.DRAFT -> draftStandingComparator.compare(left.standing, right.standing)
            }
            fallback.takeIf { it != 0 } ?: left.team.displayName.compareTo(right.team.displayName)
        }
    }
}

private fun compareInts(
    left: Int,
    right: Int,
    direction: StandingsSortDirection,
): Int = when (direction) {
    StandingsSortDirection.ASC -> left.compareTo(right)
    StandingsSortDirection.DESC -> right.compareTo(left)
}

private fun compareStrings(
    left: String,
    right: String,
    direction: StandingsSortDirection,
): Int = when (direction) {
    StandingsSortDirection.ASC -> left.compareTo(right)
    StandingsSortDirection.DESC -> right.compareTo(left)
}

@Composable
fun BlockedShotsChartPage(
    response: BlockedShotsChartResponse?,
    selectedChart: NhlChartRoute,
    selectedMissesMode: BlockedShotsMissesMode,
    onSelectChart: (NhlChartRoute) -> Unit,
    onSelectMissesMode: (BlockedShotsMissesMode) -> Unit,
    isLoading: Boolean,
    errorMessage: String?,
    teamDetails: Map<String, BlockedShotsTeamDetailResponse>,
    teamDetailLoadingTeamIds: Set<String>,
    teamDetailErrors: Map<String, String>,
    csvHref: String,
    copyButtonLabel: String,
    onCopyUrl: () -> Unit,
    onLoadTeamDetail: (String) -> Unit,
) {
    var rawDataExpanded by remember { mutableStateOf(false) }
    var expandedTeamIds by remember(response?.selectedMissesMode, response?.snapshotGeneratedAtIso) {
        mutableStateOf(setOf<String>())
    }
    var openOverlapGroupId by remember(response?.selectedMissesMode, response?.snapshotGeneratedAtIso, selectedChart) {
        mutableStateOf<String?>(null)
    }
    var chartShellViewport by remember(selectedChart, response?.selectedMissesMode, response?.snapshotGeneratedAtIso) {
        mutableStateOf(BlockedShotsChartShellViewport())
    }

    if (selectedChart.usesBlockedShotsScatter() && response != null) {
        DisposableEffect(selectedChart, response.selectedMissesMode, response.snapshotGeneratedAtIso) {
            val syncChartShellViewport: () -> Unit = syncChartShellViewport@{
                val chartShell = document.getElementById(BLOCKED_CHART_SHELL_ID) ?: return@syncChartShellViewport
                val rect = chartShell.asDynamic().getBoundingClientRect()
                chartShellViewport = BlockedShotsChartShellViewport(
                    widthPx = (rect.width as Number).toDouble(),
                    heightPx = (rect.height as Number).toDouble(),
                )
            }
            val resizeHandler: (Event) -> Unit = {
                syncChartShellViewport()
            }

            syncChartShellViewport()
            window.addEventListener("resize", resizeHandler)
            onDispose {
                window.removeEventListener("resize", resizeHandler)
            }
        }

        DisposableEffect(openOverlapGroupId, selectedChart, response.selectedMissesMode, response.snapshotGeneratedAtIso) {
            val currentGroupId = openOverlapGroupId
            if (currentGroupId == null) {
                return@DisposableEffect onDispose { }
            }

            val outsideClickHandler: (Event) -> Unit = outsideClickHandler@{ event ->
                val targetNode = event.target as? Node ?: return@outsideClickHandler
                val activeGroupElement = document.getElementById(blockedChartOverlapGroupElementId(currentGroupId))
                if (activeGroupElement?.contains(targetNode) != true) {
                    openOverlapGroupId = null
                }
            }
            val keydownHandler: (Event) -> Unit = keydownHandler@{ event ->
                val keyboardEvent = event as? KeyboardEvent ?: return@keydownHandler
                if (keyboardEvent.key == "Escape") {
                    openOverlapGroupId = null
                }
            }

            document.addEventListener("mousedown", outsideClickHandler)
            document.addEventListener("touchstart", outsideClickHandler)
            window.addEventListener("keydown", keydownHandler)
            onDispose {
                document.removeEventListener("mousedown", outsideClickHandler)
                document.removeEventListener("touchstart", outsideClickHandler)
                window.removeEventListener("keydown", keydownHandler)
            }
        }
    }

    Div(attrs = { classes("important-card", "charts-card") }) {
        H2 { Text("Charts") }
        Div(attrs = { classes("charts-tab-row") }) {
            NhlChartRoute.entries.forEach { chart ->
                Button(
                    attrs = {
                        classes("charts-tab-button")
                        if (chart == selectedChart) {
                            classes("charts-tab-button-active")
                        }
                        onClick { onSelectChart(chart) }
                    },
                ) {
                    Text(chart.title)
                }
            }
        }
        Div(attrs = { classes("charts-tab-row") }) {
            listOf(BlockedShotsMissesMode.INCLUDE, BlockedShotsMissesMode.EXCLUDE).forEach { missesMode ->
                Button(
                    attrs = {
                        classes("charts-tab-button")
                        if (missesMode == selectedMissesMode) {
                            classes("charts-tab-button-active")
                        }
                        onClick { onSelectMissesMode(missesMode) }
                    },
                ) {
                    Text(missesMode.displayLabel())
                }
            }
        }

        when {
            isLoading -> {
                P(attrs = { classes("meta-line") }) { Text("Loading blocked-shot charts...") }
            }

            errorMessage != null -> {
                P(attrs = { classes("meta-error") }) { Text(errorMessage) }
            }

            response == null -> {
                P(attrs = { classes("meta-line") }) { Text("Loading blocked-shot charts...") }
            }

            else -> {
                val chartSpec = blockedShotsChartSpec(selectedChart, response.selectedMissesMode)
                val scatterMetrics = blockedShotsScatterMetrics(response.rows, selectedChart)
                val scatterPresentation = blockedShotsScatterPresentation(
                    points = scatterMetrics.points,
                    chartShellViewport = chartShellViewport,
                )
                val blockShareRows = blockedShotsBarRows(response.rows, selectedChart)
                val sortedRawRows = blockedShotsRawTableRows(response.rows, selectedChart)
                val blockShareScaleMax = blockedShotsBarScaleMax(response.rows, selectedChart)
                LaunchedEffect(
                    selectedChart,
                    response.selectedMissesMode,
                    response.snapshotGeneratedAtIso,
                    scatterPresentation.groupIdsKey,
                ) {
                    if (openOverlapGroupId != null && scatterPresentation.groups.none { it.id == openOverlapGroupId }) {
                        openOverlapGroupId = null
                    }
                }
                P(attrs = { classes("meta-line") }) {
                    Text(chartSpec.headline)
                }
                chartSpec.note?.let { note ->
                    P(attrs = { classes("meta-line") }) { Text(note) }
                }
                P(attrs = { classes("meta-line") }) {
                    Text("Misses: ${response.selectedMissesModeLabel} | Source: ${response.source} | Season ${formatSeasonLabel(response.seasonId)} | Snapshot ${response.snapshotGeneratedAtIso}")
                }

                H3(attrs = { classes("charts-section-title") }) { Text(chartSpec.title) }

                when (selectedChart) {
                    NhlChartRoute.BLOCKED_SHOTS_CONTEXT -> {
                        Div(attrs = { classes("blocked-chart-layout") }) {
                            Div(attrs = { classes("blocked-chart-body") }) {
                                Div(attrs = { classes("blocked-chart-axis-gutter") }) {
                                    Div(attrs = { classes("blocked-chart-axis-label", "blocked-chart-axis-label-y") }) {
                                        Text(chartSpec.yAxisLabel ?: "")
                                    }
                                }
                                Div(
                                    attrs = {
                                        classes("blocked-chart-shell")
                                        attr("id", BLOCKED_CHART_SHELL_ID)
                                    },
                                ) {
                                    Div(attrs = { classes("blocked-chart-surface") })
                                    Div(attrs = { classes("blocked-chart-axis-layer") }) {
                                        scatterMetrics.xGuidePercent?.let { xGuidePercent ->
                                            Div(
                                                attrs = {
                                                    classes("blocked-chart-axis-guide", "blocked-chart-axis-guide-y")
                                                    style {
                                                        property("left", "${formatChartPercent(xGuidePercent)}%")
                                                        property("bottom", "${formatChartPercent(scatterMetrics.yAxisStartPercent)}%")
                                                        property(
                                                            "height",
                                                            "${formatChartPercent(scatterMetrics.yAxisEndPercent - scatterMetrics.yAxisStartPercent)}%",
                                                        )
                                                    }
                                                },
                                            )
                                        }
                                        scatterMetrics.yGuidePercent?.let { yGuidePercent ->
                                            Div(
                                                attrs = {
                                                    classes("blocked-chart-axis-guide", "blocked-chart-axis-guide-x")
                                                    style {
                                                        property("left", "${formatChartPercent(scatterMetrics.xAxisStartPercent)}%")
                                                        property("bottom", "${formatChartPercent(yGuidePercent)}%")
                                                        property(
                                                            "width",
                                                            "${formatChartPercent(scatterMetrics.xAxisEndPercent - scatterMetrics.xAxisStartPercent)}%",
                                                        )
                                                    }
                                                },
                                            )
                                        }
                                        Div(
                                            attrs = {
                                                classes("blocked-chart-axis-line", "blocked-chart-axis-line-x")
                                                style {
                                                    property("left", "${formatChartPercent(scatterMetrics.xAxisStartPercent)}%")
                                                    property(
                                                        "width",
                                                        "${formatChartPercent(scatterMetrics.xAxisEndPercent - scatterMetrics.xAxisStartPercent)}%",
                                                    )
                                                    property("bottom", "${formatChartPercent(scatterMetrics.yAxisStartPercent)}%")
                                                }
                                            },
                                        )
                                        Div(
                                            attrs = {
                                                classes("blocked-chart-axis-line", "blocked-chart-axis-line-y")
                                                style {
                                                    property("left", "${formatChartPercent(scatterMetrics.xAxisStartPercent)}%")
                                                    property("bottom", "${formatChartPercent(scatterMetrics.yAxisStartPercent)}%")
                                                    property(
                                                        "height",
                                                        "${formatChartPercent(scatterMetrics.yAxisEndPercent - scatterMetrics.yAxisStartPercent)}%",
                                                    )
                                                }
                                            },
                                        )
                                        scatterMetrics.xTicks.forEach { tick ->
                                            Div(
                                                attrs = {
                                                    classes("blocked-chart-tick", "blocked-chart-tick-x")
                                                    when (tick.anchor) {
                                                        BlockedShotsAxisTickAnchor.START -> classes("blocked-chart-tick-anchor-start")
                                                        BlockedShotsAxisTickAnchor.CENTER -> classes("blocked-chart-tick-anchor-center")
                                                        BlockedShotsAxisTickAnchor.END -> classes("blocked-chart-tick-anchor-end")
                                                    }
                                                    style {
                                                        property("left", "${formatChartPercent(tick.percent)}%")
                                                        property("bottom", "${formatChartPercent(scatterMetrics.yAxisStartPercent)}%")
                                                    }
                                                },
                                            ) {
                                                Div(attrs = { classes("blocked-chart-tick-mark") })
                                                Div(attrs = { classes("blocked-chart-tick-label") }) {
                                                    Text(tick.label)
                                                }
                                            }
                                        }
                                        scatterMetrics.yTicks.forEach { tick ->
                                            Div(
                                                attrs = {
                                                    classes("blocked-chart-tick", "blocked-chart-tick-y")
                                                    style {
                                                        property("left", "${formatChartPercent(scatterMetrics.xAxisStartPercent)}%")
                                                        property("bottom", "${formatChartPercent(tick.percent)}%")
                                                    }
                                                },
                                            ) {
                                                Div(attrs = { classes("blocked-chart-tick-label") }) {
                                                    Text(tick.label)
                                                }
                                                Div(attrs = { classes("blocked-chart-tick-mark") })
                                            }
                                        }
                                    }
                                    Div(attrs = { classes("blocked-chart-plot") }) {
                                        scatterPresentation.singlePoints.forEach { point ->
                                            val tooltipClasses = blockedShotsTooltipClasses(
                                                xPercent = point.xPercent,
                                                yPercent = point.yPercent,
                                            )
                                            Div(
                                                attrs = {
                                                    classes("blocked-chart-point")
                                                    tooltipClasses.forEach { classes(it) }
                                                    style {
                                                        property("left", "${formatChartPercent(point.xPercent)}%")
                                                        property("bottom", "${formatChartPercent(point.yPercent)}%")
                                                    }
                                                },
                                            ) {
                                                Button(
                                                    attrs = {
                                                        classes("blocked-chart-point-button")
                                                        attr("aria-label", blockedShotsScatterAriaLabel(point.row, selectedChart, response.selectedMissesMode))
                                                    },
                                                ) {
                                                    BlockedChartTeamLogo(
                                                        team = point.row.team,
                                                        shellClassName = "blocked-chart-point-logo-shell",
                                                        logoClassName = "blocked-chart-point-logo",
                                                    )
                                                }
                                                Div(attrs = { classes("blocked-chart-tooltip") }) {
                                                    P(attrs = { classes("blocked-chart-tooltip-title") }) { Text(point.row.team.displayName) }
                                                    blockedShotsScatterTooltipLines(point.row, selectedChart, response.selectedMissesMode).forEach { line ->
                                                        P(attrs = { classes("blocked-chart-tooltip-line") }) {
                                                            Text(line)
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                        scatterPresentation.groups.forEach { group ->
                                            val overlayClasses = blockedShotsTooltipClasses(
                                                xPercent = group.xPercent,
                                                yPercent = group.yPercent,
                                            )
                                            val isOpen = openOverlapGroupId == group.id
                                            Div(
                                                attrs = {
                                                    classes("blocked-chart-point", "blocked-chart-point-stack")
                                                    if (isOpen) {
                                                        classes("blocked-chart-point-open")
                                                    }
                                                    overlayClasses.forEach { classes(it) }
                                                    attr("id", blockedChartOverlapGroupElementId(group.id))
                                                    onMouseLeave {
                                                        if (openOverlapGroupId == group.id) {
                                                            openOverlapGroupId = null
                                                        }
                                                    }
                                                    style {
                                                        property("left", "${formatChartPercent(group.xPercent)}%")
                                                        property("bottom", "${formatChartPercent(group.yPercent)}%")
                                                        property("width", "${formatOneDecimal(group.triggerWidthPx)}px")
                                                        property("height", "${formatOneDecimal(group.triggerHeightPx)}px")
                                                    }
                                                },
                                            ) {
                                                Div(
                                                    attrs = {
                                                        classes("blocked-chart-point-stack-cluster")
                                                    },
                                                ) {
                                                    group.members.forEachIndexed { index, member ->
                                                        Div(
                                                            attrs = {
                                                                classes("blocked-chart-point-stack-member")
                                                                style {
                                                                    property(
                                                                        "transform",
                                                                        "translate(-50%, 50%) translate(${formatOneDecimal(member.xOffsetPx)}px, ${formatOneDecimal(-member.yOffsetPx)}px)",
                                                                    )
                                                                    property("z-index", (index + 1).toString())
                                                                }
                                                            },
                                                        ) {
                                                            BlockedChartTeamLogo(
                                                                team = member.row.team,
                                                                shellClassName = "blocked-chart-point-logo-shell",
                                                                logoClassName = "blocked-chart-point-logo",
                                                            )
                                                        }
                                                    }
                                                }
                                                Button(
                                                    attrs = {
                                                        classes("blocked-chart-point-group-trigger")
                                                        attr("aria-haspopup", "dialog")
                                                        attr("aria-expanded", isOpen.toString())
                                                        attr(
                                                            "aria-label",
                                                            "${group.members.size} teams overlap here: ${group.members.joinToString { it.row.team.displayName }}",
                                                        )
                                                        onMouseEnter {
                                                            openOverlapGroupId = group.id
                                                        }
                                                        onFocusIn {
                                                            openOverlapGroupId = group.id
                                                        }
                                                        onClick {
                                                            openOverlapGroupId = group.id
                                                        }
                                                    },
                                                ) {}
                                                if (isOpen) {
                                                    Div(
                                                        attrs = {
                                                            classes("blocked-chart-stack-picker")
                                                            attr("role", "dialog")
                                                            attr("aria-label", "Teams stacked at this chart position")
                                                        },
                                                    ) {
                                                        group.members.forEach { member ->
                                                            Div(
                                                                attrs = {
                                                                    classes("blocked-chart-stack-row")
                                                                    attr("role", "group")
                                                                    attr("tabindex", "0")
                                                                },
                                                            ) {
                                                                BlockedChartTeamLogo(
                                                                    team = member.row.team,
                                                                    shellClassName = "blocked-chart-stack-row-logo-shell",
                                                                    logoClassName = "blocked-chart-stack-row-logo",
                                                                )
                                                                Div(attrs = { classes("blocked-chart-stack-row-content") }) {
                                                                    P(attrs = { classes("blocked-chart-stack-row-title") }) {
                                                                        Text(member.row.team.displayName)
                                                                    }
                                                                    blockedShotsScatterTooltipLines(member.row, selectedChart, response.selectedMissesMode).forEach { line ->
                                                                        P(attrs = { classes("blocked-chart-stack-row-line") }) {
                                                                            Text(line)
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            Div(attrs = { classes("blocked-chart-main") }) {
                                Div(attrs = { classes("blocked-chart-axis-label", "blocked-chart-axis-label-x") }) {
                                    Text(chartSpec.xAxisLabel ?: "")
                                }
                            }
                        }
                    }

                    NhlChartRoute.BLOCKED_SHOTS_PERCENT -> {
                        Div(attrs = { classes("block-share-chart") }) {
                            blockShareRows.forEach { row ->
                                val barValue = blockedShotsBarValue(row, selectedChart)
                                val widthPercent = ((barValue / blockShareScaleMax) * 100.0).coerceIn(0.0, 100.0)
                                Div(attrs = { classes("block-share-row") }) {
                                    Div(
                                        attrs = {
                                            classes("block-share-track")
                                            attr("style", TeamTilePalette.inlineStyle(row.team.id))
                                        },
                                    ) {
                                        Div(
                                            attrs = {
                                                classes("block-share-fill")
                                                style {
                                                    property("width", "${formatChartPercent(widthPercent)}%")
                                                }
                                            },
                                        ) {
                                            Span(attrs = { classes("block-share-abbr") }) {
                                                Text(row.team.abbreviation)
                                            }
                                        }
                                    }
                                    Span(attrs = { classes("block-share-value") }) {
                                        Text(blockedShotsBarValueLabel(row, selectedChart))
                                    }
                                }
                            }
                        }
                    }
                }

                Div(attrs = { classes("charts-action-row") }) {
                    Button(
                        attrs = {
                            classes("pulse-button")
                            onClick { onCopyUrl() }
                        },
                    ) {
                        Text(copyButtonLabel)
                    }
                    Button(
                        attrs = {
                            classes("pulse-button")
                            onClick { rawDataExpanded = !rawDataExpanded }
                        },
                    ) {
                        Text(if (rawDataExpanded) "Hide Raw Data" else "View Raw Data")
                    }
                    A(
                        href = csvHref,
                        attrs = {
                            classes("pulse-button", "charts-download-link")
                            attr("download", "playoffpulse-blocked-shots.csv")
                        },
                    ) {
                        Text("Download CSV")
                    }
                }

                if (rawDataExpanded) {
                    Div(attrs = { classes("standings-table-wrap", "charts-table-wrap") }) {
                        Table(attrs = { classes("standings-table", "charts-table") }) {
                            Thead {
                                Tr {
                                    Th { Text("Team") }
                                    Th { Text(if (response.selectedMissesMode == BlockedShotsMissesMode.EXCLUDE) "Shots/G" else "Attempts/G") }
                                    Th { Text("Blocks/G") }
                                    Th { Text(if (response.selectedMissesMode == BlockedShotsMissesMode.EXCLUDE) "Shots" else "Attempts") }
                                    Th { Text("Blocks") }
                                    Th { Text(if (response.selectedMissesMode == BlockedShotsMissesMode.EXCLUDE) "On Goal/G" else "Unblocked/G") }
                                    Th { Text("Block Share") }
                                    Th { Text("GP") }
                                }
                            }
                            Tbody {
                                sortedRawRows.forEach { row ->
                                    val teamId = row.team.id.uppercase()
                                    val detail = teamDetails[teamId]
                                    val isExpanded = teamId in expandedTeamIds
                                    val isDetailLoading = teamId in teamDetailLoadingTeamIds
                                    val detailError = teamDetailErrors[teamId]
                                    Tr {
                                        Td {
                                            Div(attrs = { classes("charts-team-stack") }) {
                                                Div(attrs = { classes("charts-team-cell") }) {
                                                    Img(
                                                        src = row.team.logoUrl ?: TeamTilePalette.logoUrl(row.team.id),
                                                        attrs = {
                                                            classes("charts-team-logo")
                                                            attr("alt", "${row.team.displayName} logo")
                                                            attr("loading", "lazy")
                                                        },
                                                    )
                                                    Text(row.team.displayName)
                                                }
                                                Button(
                                                    attrs = {
                                                        classes("charts-row-toggle")
                                                        onClick {
                                                            expandedTeamIds = if (isExpanded) {
                                                                expandedTeamIds - teamId
                                                            } else {
                                                                expandedTeamIds + teamId
                                                            }
                                                            if (!isExpanded && detail == null && !isDetailLoading) {
                                                                onLoadTeamDetail(teamId)
                                                            }
                                                        }
                                                    },
                                                ) {
                                                    Text(if (isExpanded) "Hide Games" else "Show Games")
                                                }
                                            }
                                        }
                                        Td { Text(formatOneDecimal(row.shotAttemptsAgainstPerGame)) }
                                        Td { Text(formatOneDecimal(row.blockedAttemptsPerGame)) }
                                        Td { Text(row.shotAttemptsAgainst.toString()) }
                                        Td { Text(row.blockedAttempts.toString()) }
                                        Td { Text(formatOneDecimal(row.unblockedAttemptsAgainstPerGame)) }
                                        Td { Text(formatPercent(row.blockShareOfShotAttempts)) }
                                        Td { Text(row.gamesPlayed.toString()) }
                                    }
                                    if (isExpanded) {
                                        Tr(attrs = { classes("charts-detail-row") }) {
                                            Td(attrs = { attr("colspan", "8") }) {
                                                Div(attrs = { classes("charts-team-detail-panel") }) {
                                                    when {
                                                        isDetailLoading -> {
                                                            P(attrs = { classes("meta-line", "charts-team-detail-status") }) {
                                                                Text("Loading game breakdown...")
                                                            }
                                                        }

                                                        detailError != null -> {
                                                            P(attrs = { classes("meta-error", "charts-team-detail-status") }) {
                                                                Text(detailError)
                                                            }
                                                        }

                                                        detail == null -> {
                                                            P(attrs = { classes("meta-line", "charts-team-detail-status") }) {
                                                                Text("Loading game breakdown...")
                                                            }
                                                        }

                                                        detail.games.isEmpty() -> {
                                                            P(attrs = { classes("meta-line", "charts-team-detail-status") }) {
                                                                Text("No completed games were available for this team yet.")
                                                            }
                                                        }

                                                        else -> {
                                                            Div(attrs = { classes("charts-team-detail-table-wrap") }) {
                                                                Table(attrs = { classes("charts-team-detail-table") }) {
                                                                    Thead {
                                                                        Tr {
                                                                            Th { Text("Date") }
                                                                            Th { Text("Opp") }
                                                                            Th { Text("Site") }
                                                                            Th { Text("Score") }
                                                                            Th { Text(if (detail.selectedMissesMode == BlockedShotsMissesMode.EXCLUDE) "Shots" else "Attempts") }
                                                                            Th { Text("Blocks") }
                                                                            Th { Text(if (detail.selectedMissesMode == BlockedShotsMissesMode.EXCLUDE) "On Goal" else "Unblocked") }
                                                                            Th { Text("Misses") }
                                                                            Th { Text("Saves") }
                                                                            Th { Text("Goals") }
                                                                            Th { Text("Block Share") }
                                                                            Th { Text("Source") }
                                                                        }
                                                                    }
                                                                    Tbody {
                                                                        detail.games.forEach { game ->
                                                                            Tr {
                                                                                Td { Text(formatChartGameDate(game.startTimeIso)) }
                                                                                Td { Text(game.opponent.abbreviation) }
                                                                                Td { Text(if (game.isHome) "Home" else "Away") }
                                                                                Td { Text(formatChartGameScore(game)) }
                                                                                Td { Text(game.shotAttemptsAgainst.toString()) }
                                                                                Td { Text(game.blockedAttempts.toString()) }
                                                                                Td { Text(game.unblockedAttemptsAgainst.toString()) }
                                                                                Td { Text(game.missedAttemptsAgainst.toString()) }
                                                                                Td { Text(game.savedAttemptsAgainst.toString()) }
                                                                                Td { Text(game.goalsAgainst.toString()) }
                                                                                Td { Text(formatPercent(game.blockShareOfShotAttempts)) }
                                                                                Td {
                                                                                    A(
                                                                                        href = game.sourceUrl,
                                                                                        attrs = {
                                                                                            classes("charts-source-link")
                                                                                            attr("target", "_blank")
                                                                                            attr("rel", "noopener noreferrer")
                                                                                        },
                                                                                    ) {
                                                                                        Text("NHL API")
                                                                                    }
                                                                                }
                                                                            }
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun BlockedChartTeamLogo(
    team: TeamRef,
    shellClassName: String,
    logoClassName: String,
) {
    Div(
        attrs = {
            classes(shellClassName)
            if (team.isTampaBayLightning()) {
                classes("${shellClassName}-lightning")
            }
        },
    ) {
        Img(
            src = team.logoUrl ?: TeamTilePalette.logoUrl(team.id),
            attrs = {
                classes(logoClassName)
                if (team.isTampaBayLightning()) {
                    classes("${logoClassName}-lightning")
                }
                attr("alt", "${team.displayName} logo")
                attr("loading", "lazy")
                attr("draggable", "false")
            },
        )
    }
}

internal data class BlockedShotsPlotPoint(
    val row: BlockedShotsChartRow,
    val xPercent: Double,
    val yPercent: Double,
)

internal data class BlockedShotsChartShellViewport(
    val widthPx: Double = 0.0,
    val heightPx: Double = 0.0,
)

internal data class BlockedShotsOverlapGroup(
    val id: String,
    val members: List<BlockedShotsOverlapGroupMember>,
    val leadPoint: BlockedShotsPlotPoint,
    val xPercent: Double,
    val yPercent: Double,
    val triggerWidthPx: Double,
    val triggerHeightPx: Double,
)

internal data class BlockedShotsOverlapGroupMember(
    val row: BlockedShotsChartRow,
    val xOffsetPx: Double,
    val yOffsetPx: Double,
)

internal data class BlockedShotsScatterPresentation(
    val singlePoints: List<BlockedShotsPlotPoint>,
    val groups: List<BlockedShotsOverlapGroup>,
) {
    val groupIdsKey: String = groups.joinToString("|") { it.id }
}

private data class BlockedShotsAxisTick(
    val label: String,
    val percent: Double,
    val anchor: BlockedShotsAxisTickAnchor = BlockedShotsAxisTickAnchor.CENTER,
)

private enum class BlockedShotsAxisTickAnchor {
    START,
    CENTER,
    END,
}

private data class BlockedShotsChartSpec(
    val headline: String,
    val note: String? = null,
    val title: String,
    val xAxisLabel: String? = null,
    val yAxisLabel: String? = null,
)

private data class BlockedShotsScatterMetrics(
    val points: List<BlockedShotsPlotPoint>,
    val xTicks: List<BlockedShotsAxisTick>,
    val yTicks: List<BlockedShotsAxisTick>,
    val xAxisStartPercent: Double,
    val xAxisEndPercent: Double,
    val yAxisStartPercent: Double,
    val yAxisEndPercent: Double,
    val xGuidePercent: Double? = null,
    val yGuidePercent: Double? = null,
)

internal fun blockedShotsScatterPresentation(
    points: List<BlockedShotsPlotPoint>,
    chartShellViewport: BlockedShotsChartShellViewport,
): BlockedShotsScatterPresentation {
    if (points.isEmpty()) {
        return BlockedShotsScatterPresentation(
            singlePoints = emptyList(),
            groups = emptyList(),
        )
    }

    val effectiveWidthPx = chartShellViewport.widthPx.takeIf { it > 0.0 } ?: BLOCKED_CHART_DEFAULT_SHELL_WIDTH_PX
    val effectiveHeightPx = chartShellViewport.heightPx.takeIf { it > 0.0 } ?: BLOCKED_CHART_DEFAULT_SHELL_HEIGHT_PX
    val overlapDistancePx = blockedChartVisibleLogoDiameterPx(effectiveWidthPx) / 2.0
    val adjacency = Array(points.size) { mutableListOf<Int>() }

    for (index in points.indices) {
        for (otherIndex in (index + 1) until points.size) {
            if (blockedShotsPointsOverlap(points[index], points[otherIndex], effectiveWidthPx, effectiveHeightPx, overlapDistancePx)) {
                adjacency[index] += otherIndex
                adjacency[otherIndex] += index
            }
        }
    }

    val visited = BooleanArray(points.size)
    val singlePoints = mutableListOf<BlockedShotsPlotPoint>()
    val groups = mutableListOf<BlockedShotsOverlapGroup>()

    for (index in points.indices) {
        if (visited[index]) continue

        val stack = ArrayDeque<Int>()
        val componentIndices = mutableListOf<Int>()
        stack.addLast(index)
        visited[index] = true

        while (stack.isNotEmpty()) {
            val current = stack.removeLast()
            componentIndices += current
            adjacency[current].forEach { neighbor ->
                if (!visited[neighbor]) {
                    visited[neighbor] = true
                    stack.addLast(neighbor)
                }
            }
        }

        val componentPoints = componentIndices.map { points[it] }
        if (componentPoints.size == 1) {
            singlePoints += componentPoints.first()
            continue
        }

        groups += blockedShotsOverlapGroup(
            points = componentPoints,
            shellWidthPx = effectiveWidthPx,
            shellHeightPx = effectiveHeightPx,
        )
    }

    return BlockedShotsScatterPresentation(
        singlePoints = singlePoints.sortedWith(blockedShotsPointComparator()),
        groups = groups.sortedBy { it.leadPoint.row.team.displayName.lowercase() },
    )
}

private fun blockedShotsChartSpec(
    chart: NhlChartRoute,
    missesMode: BlockedShotsMissesMode,
): BlockedShotsChartSpec {
    val countLabel = if (missesMode == BlockedShotsMissesMode.EXCLUDE) {
        "Shots"
    } else {
        "Attempts"
    }
    return when (chart) {
        NhlChartRoute.BLOCKED_SHOTS_CONTEXT -> BlockedShotsChartSpec(
            headline = "This view pairs $countLabel allowed with blocks so low raw block totals can be read in context.",
            title = "Attempts vs. Blocks",
            xAxisLabel = "$countLabel Allowed Per Game",
            yAxisLabel = "Blocks Per Game",
        )
        NhlChartRoute.BLOCKED_SHOTS_PERCENT -> BlockedShotsChartSpec(
            headline = "This view normalizes blocks by opportunity, showing the share of $countLabel that get blocked.",
            title = "% Blocked",
        )
    }
}

private fun NhlChartRoute.usesBlockedShotsScatter(): Boolean = when (this) {
    NhlChartRoute.BLOCKED_SHOTS_CONTEXT -> true
    NhlChartRoute.BLOCKED_SHOTS_PERCENT -> false
}

private fun blockedShotsScatterMetrics(
    rows: List<BlockedShotsChartRow>,
    chart: NhlChartRoute,
): BlockedShotsScatterMetrics {
    val xAxisStartPercent = BLOCKED_CHART_LEFT_PADDING
    val xAxisEndPercent = BLOCKED_CHART_LEFT_PADDING + BLOCKED_CHART_USABLE_WIDTH
    val yAxisStartPercent = BLOCKED_CHART_BOTTOM_PADDING
    val yAxisEndPercent = BLOCKED_CHART_BOTTOM_PADDING + BLOCKED_CHART_USABLE_HEIGHT

    if (rows.isEmpty()) {
        return BlockedShotsScatterMetrics(
            points = emptyList(),
            xTicks = emptyList(),
            yTicks = emptyList(),
            xAxisStartPercent = xAxisStartPercent,
            xAxisEndPercent = xAxisEndPercent,
            yAxisStartPercent = yAxisStartPercent,
            yAxisEndPercent = yAxisEndPercent,
        )
    }

    val xValues = rows.map { blockedShotsScatterXValue(it, chart) }
    val yValues = rows.map { blockedShotsScatterYValue(it, chart) }
    val xMin = xValues.minOrNull() ?: 0.0
    val xMax = xValues.maxOrNull() ?: 0.0
    val yMin = yValues.minOrNull() ?: 0.0
    val yMax = yValues.maxOrNull() ?: 0.0

    val points = rows.map { row ->
        val xValue = blockedShotsScatterXValue(row, chart)
        val yValue = blockedShotsScatterYValue(row, chart)
        val normalizedX = normalizeChartValue(xValue, xMin, xMax)
        val normalizedY = normalizeChartValue(yValue, yMin, yMax)
        BlockedShotsPlotPoint(
            row = row,
            xPercent = xAxisStartPercent + ((1 - normalizedX) * BLOCKED_CHART_USABLE_WIDTH),
            yPercent = yAxisStartPercent + (normalizedY * BLOCKED_CHART_USABLE_HEIGHT),
        )
    }

    val xGuideValue = xValues.averageOrNull()
    val yGuideValue = yValues.averageOrNull()

    return BlockedShotsScatterMetrics(
        points = points,
        xTicks = listOf(
            BlockedShotsAxisTick(
                label = blockedShotsScatterTickLabel(xMax, chart),
                percent = xAxisStartPercent,
                anchor = BlockedShotsAxisTickAnchor.START,
            ),
            BlockedShotsAxisTick(
                label = blockedShotsScatterTickLabel((xMin + xMax) / 2.0, chart),
                percent = (xAxisStartPercent + xAxisEndPercent) / 2.0,
            ),
            BlockedShotsAxisTick(
                label = blockedShotsScatterTickLabel(xMin, chart),
                percent = xAxisEndPercent,
                anchor = BlockedShotsAxisTickAnchor.END,
            ),
        ),
        yTicks = listOf(
            BlockedShotsAxisTick(
                label = formatWholeNumber(yMin),
                percent = yAxisStartPercent,
            ),
            BlockedShotsAxisTick(
                label = formatWholeNumber((yMin + yMax) / 2.0),
                percent = (yAxisStartPercent + yAxisEndPercent) / 2.0,
            ),
            BlockedShotsAxisTick(
                label = formatWholeNumber(yMax),
                percent = yAxisEndPercent,
            ),
        ),
        xAxisStartPercent = xAxisStartPercent,
        xAxisEndPercent = xAxisEndPercent,
        yAxisStartPercent = yAxisStartPercent,
        yAxisEndPercent = yAxisEndPercent,
        xGuidePercent = xGuideValue?.let {
            xAxisStartPercent + ((1 - normalizeChartValue(it, xMin, xMax)) * BLOCKED_CHART_USABLE_WIDTH)
        },
        yGuidePercent = yGuideValue?.let {
            yAxisStartPercent + (normalizeChartValue(it, yMin, yMax) * BLOCKED_CHART_USABLE_HEIGHT)
        },
    )
}

private const val BLOCKED_CHART_LEFT_PADDING = 8.0
private const val BLOCKED_CHART_BOTTOM_PADDING = 9.0
private const val BLOCKED_CHART_USABLE_WIDTH = 84.0
private const val BLOCKED_CHART_USABLE_HEIGHT = 82.0
private const val BLOCKED_CHART_DEFAULT_SHELL_WIDTH_PX = 720.0
private const val BLOCKED_CHART_DEFAULT_SHELL_HEIGHT_PX = 496.0
private const val BLOCKED_CHART_SHELL_ID = "blocked-chart-shell"

internal fun blockedShotsRawTableRows(
    rows: List<BlockedShotsChartRow>,
    chart: NhlChartRoute,
): List<BlockedShotsChartRow> {
    val comparator = compareBy<BlockedShotsChartRow> { it.team.displayName.lowercase() }
        .thenBy { it.team.abbreviation.uppercase() }
        .thenBy { it.team.id.uppercase() }
    return rows.sortedWith(comparator)
}

private fun blockedShotsPointsOverlap(
    left: BlockedShotsPlotPoint,
    right: BlockedShotsPlotPoint,
    shellWidthPx: Double,
    shellHeightPx: Double,
    overlapDistancePx: Double,
): Boolean {
    val xDistancePx = ((left.xPercent - right.xPercent) / 100.0) * shellWidthPx
    val yDistancePx = ((left.yPercent - right.yPercent) / 100.0) * shellHeightPx
    return hypot(xDistancePx, yDistancePx) < overlapDistancePx
}

private fun blockedChartVisibleLogoDiameterPx(shellWidthPx: Double): Double {
    return if (shellWidthPx <= 520.0) 42.0 else 50.0
}

private fun blockedShotsPointComparator(): Comparator<BlockedShotsPlotPoint> {
    return compareBy<BlockedShotsPlotPoint> { it.row.team.displayName.lowercase() }
        .thenBy { it.row.team.abbreviation.uppercase() }
        .thenBy { it.row.team.id.uppercase() }
}

private fun blockedShotsOverlapGroup(
    points: List<BlockedShotsPlotPoint>,
    shellWidthPx: Double,
    shellHeightPx: Double,
): BlockedShotsOverlapGroup {
    val sortedMembers = points.sortedWith(blockedShotsPointComparator())
    val triggerRadiusPx = blockedChartVisibleLogoDiameterPx(shellWidthPx) / 2.0
    val memberCenters = sortedMembers.map { point ->
        point to BlockedChartPixelCoordinate(
            xPx = (point.xPercent / 100.0) * shellWidthPx,
            yPx = (point.yPercent / 100.0) * shellHeightPx,
        )
    }
    val leftPx = memberCenters.minOf { it.second.xPx - triggerRadiusPx }
    val rightPx = memberCenters.maxOf { it.second.xPx + triggerRadiusPx }
    val bottomPx = memberCenters.minOf { it.second.yPx - triggerRadiusPx }
    val topPx = memberCenters.maxOf { it.second.yPx + triggerRadiusPx }
    val centerXPx = (leftPx + rightPx) / 2.0
    val centerYPx = (bottomPx + topPx) / 2.0

    return BlockedShotsOverlapGroup(
        id = sortedMembers.joinToString("-") { it.row.team.id.uppercase() },
        members = memberCenters.map { (point, coordinate) ->
            BlockedShotsOverlapGroupMember(
                row = point.row,
                xOffsetPx = coordinate.xPx - centerXPx,
                yOffsetPx = coordinate.yPx - centerYPx,
            )
        },
        leadPoint = sortedMembers.first(),
        xPercent = (centerXPx / shellWidthPx) * 100.0,
        yPercent = (centerYPx / shellHeightPx) * 100.0,
        triggerWidthPx = rightPx - leftPx,
        triggerHeightPx = topPx - bottomPx,
    )
}

private data class BlockedChartPixelCoordinate(
    val xPx: Double,
    val yPx: Double,
)

private fun blockedShotsBarRows(
    rows: List<BlockedShotsChartRow>,
    chart: NhlChartRoute,
): List<BlockedShotsChartRow> {
    val comparator = compareByDescending<BlockedShotsChartRow> { it.blockShareOfShotAttempts }
        .thenBy { it.team.abbreviation }
    return rows.sortedWith(comparator)
}

private fun blockedShotsBarScaleMax(
    rows: List<BlockedShotsChartRow>,
    chart: NhlChartRoute,
): Double {
    return (rows.maxOfOrNull { it.blockShareOfShotAttempts } ?: 0.30).coerceAtLeast(0.30)
}

private fun blockedShotsScatterXValue(
    row: BlockedShotsChartRow,
    chart: NhlChartRoute,
): Double = when (chart) {
    NhlChartRoute.BLOCKED_SHOTS_CONTEXT -> row.shotAttemptsAgainstPerGame
    else -> row.shotAttemptsAgainstPerGame
}

private fun blockedShotsScatterYValue(
    row: BlockedShotsChartRow,
    chart: NhlChartRoute,
): Double = when (chart) {
    NhlChartRoute.BLOCKED_SHOTS_CONTEXT -> row.blockedAttemptsPerGame
    else -> row.blockedAttemptsPerGame
}

private fun blockedShotsBarValue(
    row: BlockedShotsChartRow,
    chart: NhlChartRoute,
): Double = when (chart) {
    NhlChartRoute.BLOCKED_SHOTS_PERCENT -> row.blockShareOfShotAttempts
    else -> row.blockShareOfShotAttempts
}

private fun blockedShotsBarValueLabel(
    row: BlockedShotsChartRow,
    chart: NhlChartRoute,
): String = when (chart) {
    NhlChartRoute.BLOCKED_SHOTS_PERCENT -> formatPercent(row.blockShareOfShotAttempts)
    else -> formatPercent(row.blockShareOfShotAttempts)
}

private fun blockedShotsScatterAriaLabel(
    row: BlockedShotsChartRow,
    chart: NhlChartRoute,
    missesMode: BlockedShotsMissesMode,
): String = when (chart) {
    NhlChartRoute.BLOCKED_SHOTS_CONTEXT ->
        "${row.team.displayName}: ${formatOneDecimal(row.shotAttemptsAgainstPerGame)} ${blockedShotsCountLabel(missesMode).lowercase()} allowed per game, ${formatOneDecimal(row.blockedAttemptsPerGame)} blocks per game"
    else -> row.team.displayName
}

private fun blockedShotsScatterTooltipLines(
    row: BlockedShotsChartRow,
    chart: NhlChartRoute,
    missesMode: BlockedShotsMissesMode,
): List<String> = when (chart) {
    NhlChartRoute.BLOCKED_SHOTS_CONTEXT -> listOf(
        "${blockedShotsCountLabel(missesMode)} Allowed Per Game: ${formatOneDecimal(row.shotAttemptsAgainstPerGame)}",
        "Blocks Per Game: ${formatOneDecimal(row.blockedAttemptsPerGame)}",
        "Block Share: ${formatPercent(row.blockShareOfShotAttempts)}",
    )
    else -> emptyList()
}

private fun blockedShotsScatterTickLabel(
    value: Double,
    chart: NhlChartRoute,
): String = when (chart) {
    else -> formatWholeNumber(value)
}

private fun Iterable<Double>.averageOrNull(): Double? {
    var count = 0
    var total = 0.0
    for (value in this) {
        total += value
        count += 1
    }
    return if (count == 0) null else total / count.toDouble()
}

private fun blockedShotsTooltipClasses(
    xPercent: Double,
    yPercent: Double,
): List<String> {
    val horizontal = when {
        xPercent <= 18.0 -> "blocked-chart-point-tooltip-right"
        xPercent >= 82.0 -> "blocked-chart-point-tooltip-left"
        else -> "blocked-chart-point-tooltip-center"
    }
    val vertical = if (yPercent >= 78.0) {
        "blocked-chart-point-tooltip-below"
    } else {
        "blocked-chart-point-tooltip-above"
    }
    return listOf(horizontal, vertical)
}

private fun blockedChartOverlapGroupElementId(groupId: String): String = "blocked-chart-overlap-$groupId"

private fun TeamRef.isTampaBayLightning(): Boolean = id.uppercase() == "TBL"

private fun normalizeChartValue(value: Double, minimum: Double, maximum: Double): Double {
    val spread = maximum - minimum
    return if (spread <= 0.0001) 0.5 else (value - minimum) / spread
}

private fun formatChartPercent(value: Double): String = formatOneDecimal(value)

private fun formatSeasonLabel(seasonId: String): String {
    return if (seasonId.length == 8) {
        "${seasonId.substring(0, 4)}-${seasonId.substring(6, 8)}"
    } else {
        seasonId
    }
}

private fun formatChartGameDate(iso: String): String {
    return runCatching {
        val date = Date(iso)
        if (date.getTime().isNaN()) return iso
        val options = js("({ timeZone: 'America/New_York', month: 'short', day: 'numeric' })")
        "${date.toLocaleDateString("en-US", options) as String} ET"
    }.getOrElse { iso }
}

private fun formatChartGameScore(game: BlockedShotsTeamGameRow): String {
    val teamScore = game.teamScore
    val opponentScore = game.opponentScore
    if (teamScore == null || opponentScore == null) {
        return "Final unavailable"
    }
    val resultType = game.resultType?.uppercase()
    val suffix = when (resultType) {
        "OT", "SO" -> " $resultType"
        else -> ""
    }
    return "$teamScore-$opponentScore$suffix"
}

private fun formatWholeNumber(value: Double): String = value.asDynamic().toFixed(0) as String

private fun formatOneDecimal(value: Double): String = value.asDynamic().toFixed(1) as String

private fun formatPercent(value: Double): String = "${(value * 100.0).asDynamic().toFixed(1) as String}%"

private fun blockedShotsCountLabel(missesMode: BlockedShotsMissesMode): String {
    return if (missesMode == BlockedShotsMissesMode.EXCLUDE) "Shots" else "Attempts"
}

private val liveStandingComparator =
    Comparator<TeamStanding> { left, right ->
        val leftSequence = left.leagueSequence ?: Int.MAX_VALUE
        val rightSequence = right.leagueSequence ?: Int.MAX_VALUE
        if (leftSequence != rightSequence) {
            leftSequence.compareTo(rightSequence)
        } else {
            left.teamId.compareTo(right.teamId)
        }
    }

private val draftStandingComparator =
    compareBy<TeamStanding> { it.points }
        .thenBy { it.regulationWins ?: Int.MAX_VALUE }
        .thenBy { it.regulationPlusOtWins ?: Int.MAX_VALUE }
        .thenBy { it.wins }
        .thenBy { it.teamId }

private fun signedPts(delta: Int): String {
    val sign = if (delta > 0) "+" else ""
    return "$sign${delta}pts"
}

private fun statusShort(status: MagicStatus): String = when (status) {
    MagicStatus.CLINCHED -> "CLINCHED"
    MagicStatus.IN_POSITION -> "HOLDS"
    MagicStatus.ELIMINATED -> "OUT"
    MagicStatus.CHASING -> "CHASING"
    MagicStatus.CONTROLS_DESTINY -> "LIVE"
}
